/**
 * useTeamIdentity — the Record index's Team-enrichment data source.
 *
 * The cross-Team index (RecordIndex) groups live work items by `deskId` and
 * stamps each lane with the Team's identity. The NAME + accent are resolved the
 * same way the rest of the Work surface resolves them (`useWorkFeed.deskName` +
 * `deskAccent`), so a Team reads identically here, in the drill-in, and in the
 * chat rail. This hook adds only the RICHER fields the lane header wants —
 * earned trust level, mission (the ambient-line fallback), and the actionable
 * "needs you" count — from the SAME `GET /api/desks` endpoint (the roster +
 * identity source, `routes/desks.ts`). No new backend.
 *
 * It also exposes the roster itself (`roster`) — every Team the sidebar lists
 * (`readableDeskRows`: never a `restricted` Team, and a personal Team only for
 * its owner and whoever sees every Team), work items or not — so the Teams
 * index + tree can show a just-created Team BEFORE its first work item lands
 * (the ghost-team fix: a team list derived purely from work items never shows
 * a brand-new Team).
 *
 * It refetches on the same `work_item.updated` / `tickets.invalidate` window
 * events the feed listens to, so a resolved gate reflows the needs-you counts.
 * It never mutates anything.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TeamPickedToday } from "@yagni/shared";
import { UNASSIGNED } from "./useWorkFeed.js";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "../shell/useNavDesks.js";
import { readableDeskRows } from "../shell/sidebarTeams.js";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import { captureUiError } from "../sentry.js";

/** The full lane identity RecordIndex composes (name/dot from the feed side). */
export interface TeamIdentity {
  id: string;
  name: string;
  /** Identity dot — the deterministic desk accent, never the attention amber. */
  dot: string;
  /** The Team's mission — the ambient now-line's honest fallback. */
  mission: string | null;
  /** Actionable-item count from the roster (the Feed's needs-you rule). */
  needsYou: number;
}

/** Harper's latest settled job run, as `GET /api/desks` serves it. */
export interface TeamLatestHarperRun {
  jobId: string;
  jobName: string;
  status: "completed" | "nothing_to_file" | "delivery_failed" | "failed";
  storyId: string | null;
  /** ISO timestamp of the run's creation. */
  at: string;
}

/** The subset this hook supplies (name/dot come from the feed + deskAccent). */
export interface TeamEnrichment {
  mission: string | null;
  needsYou: number;
  /**
   * Is the signed-in reader on this Team's roster? (ADR-0044 edge case 22.)
   *
   * Reading a Team is not answering for it: a gate is addressed to its
   * supervisor and answerable by any MEMBER, so the case file needs this to
   * decide whether to offer the verbs or disable them with the reason. An
   * older backend omits it and this reads false, which is the safe direction:
   * the route refuses a non-member anyway, and the card says why.
   */
  viewerIsMember: boolean;
  /**
   * May the reader change this Team's connected tools? The server decides, by
   * the same rule its write route enforces (admin, the Team's publisher or its
   * editor). Absent reads false, the safe direction: the card then offers to
   * ask the owner instead.
   */
  viewerCanManage: boolean;
}

/** One roster row: a Team's identity off `GET /api/desks`, items or not. */
export interface TeamRosterEntry extends TeamEnrichment {
  id: string;
  /** Display name, already in the customer-facing "… Team" form. */
  name: string;
  /** The Team's chosen line icon (shared TEAM_ICON_NAMES); null/absent = derived default. */
  icon?: string | null;
  /** Harper's latest settled job run, or null/absent (no run = the
   * standup row stays skeleton). Parsed defensively: an older backend simply
   * omits it, and fixtures that predate the field stay valid. */
  latestHarperRun?: TeamLatestHarperRun | null;
  /**
   * Who made the Team (the desk's publisher), so Work can tell whether the
   * reader already owns one. Null/absent when the row does not say.
   */
  publisherUserId?: string | null;
  /** "2 of 3 picked today": null with no propose line, undefined when unread. */
  pickedToday?: TeamPickedToday | null;
}

/**
 * A Team's display name for the index surfaces: the feed's shared resolver
 * (`useWorkFeed.deskName`) when it knows the desk, else the roster name. The
 * feed resolver answers "Unassigned" for a desk missing from its name map, and
 * a roster Team must never read as "Unassigned".
 */
export function rosterTeamName(
  deskId: string,
  deskName: (deskId: string | null) => string,
  rosterName: string | undefined,
): string {
  const fromFeed = deskName(deskId);
  return fromFeed === UNASSIGNED && rosterName ? rosterName : fromFeed;
}

/** Defensive parse of the run: any missing/odd field reads as no run. */
function parseLatestHarperRun(raw: unknown): TeamLatestHarperRun | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  if (
    typeof f.jobId !== "string" ||
    typeof f.jobName !== "string" ||
    !f.jobName.trim() ||
    typeof f.at !== "string" ||
    (f.status !== "completed" &&
      f.status !== "nothing_to_file" &&
      f.status !== "delivery_failed" &&
      f.status !== "failed")
  ) {
    return null;
  }
  return {
    jobId: f.jobId,
    jobName: f.jobName,
    status: f.status,
    storyId: typeof f.storyId === "string" ? f.storyId : null,
    at: f.at,
  };
}

/** The roster: exactly the Teams the sidebar lists, off `/api/desks`. */
function flattenRoster(
  payload: unknown,
  viewerUserId: string | null,
): Map<string, TeamRosterEntry> {
  const out = new Map<string, TeamRosterEntry>();
  for (const r of readableDeskRows(payload, viewerUserId)) {
    if (typeof r.id !== "string") continue;
    // The endpoint already excludes archived + implicit desks (isUsableDesk,
    // routes/desks.ts); skip archived rows defensively anyway. Paused desks
    // stay listed — the rest of the app keeps showing them.
    if (r.archivedAt) continue;
    out.set(r.id, {
      id: r.id,
      // The raw stored name, same as the feed resolver hands back.
      name: (typeof r.name === "string" && r.name.trim()) || "This team",
      icon: typeof r.icon === "string" ? r.icon : null,
      mission: typeof r.mission === "string" ? r.mission : null,
      needsYou: typeof r.needsYou === "number" ? r.needsYou : 0,
      viewerIsMember: r.viewerIsMember === true,
      viewerCanManage: r.viewerCanManage === true,
      latestHarperRun: parseLatestHarperRun(r.latestHarperRun),
      publisherUserId: typeof r.publisherUserId === "string" ? r.publisherUserId : null,
      pickedToday: parsePickedToday(r.pickedToday),
    });
  }
  return out;
}

/**
 * Today's propose-line tally (`TeamPickedToday`). Undefined when the read did
 * not carry it (an older server, a failed read), null when the Team has no
 * propose line.
 */
export function parsePickedToday(raw: unknown): TeamPickedToday | null | undefined {
  if (raw === null) return null;
  if (!raw || typeof raw !== "object") return undefined;
  const f = raw as Record<string, unknown>;
  if (typeof f.count !== "number") return undefined;
  return {
    count: f.count,
    cap: typeof f.cap === "number" ? f.cap : null,
    nextPickAt: typeof f.nextPickAt === "string" ? f.nextPickAt : null,
    timezone: typeof f.timezone === "string" ? f.timezone : null,
  };
}

export interface UseTeamIdentityResult {
  /** Enrichment for a deskId, or null when the desk is unknown to the roster. */
  enrichmentOf: (deskId: string) => TeamEnrichment | null;
  /** Every Team on the roster (`GET /api/desks`), work items or not. */
  roster: TeamRosterEntry[];
  ready: boolean;
}

export function useTeamIdentity(): UseTeamIdentityResult {
  const [rows, setRows] = useState<Map<string, TeamRosterEntry>>(new Map());
  const [ready, setReady] = useState(false);
  const viewerUserId = useAuthSession()?.user?.id ?? null;

  // A roster is one reader's: it was flattened for them (their personal Teams,
  // their membership). A new reader starts from none, so a failed first read
  // for them cannot leave the previous reader's roster standing.
  const [rosterViewer, setRosterViewer] = useState(viewerUserId);
  if (rosterViewer !== viewerUserId) {
    setRosterViewer(viewerUserId);
    setRows(new Map());
    setReady(false);
  }

  // Only the newest read lands: a slower read started for an earlier viewer
  // (or before a refetch event) must not overwrite a newer roster.
  const latestRead = useRef(0);
  const fetchAll = useCallback(() => {
    const read = ++latestRead.current;
    let status: number | null = null;
    fetch("/api/desks", { credentials: "include" })
      .then((res) => {
        status = res.status;
        // A failed read is not an empty roster: an empty one reads every Team
        // as one the viewer is not on.
        if (!res.ok) throw new Error(`desks read failed (${res.status})`);
        return res.json();
      })
      .then((data: unknown) => {
        if (read === latestRead.current) setRows(flattenRoster(data, viewerUserId));
      })
      .catch((err: unknown) => {
        // The previous roster stays; the failure is reported, so a card that
        // offers the wrong verbs can be traced to the read behind it.
        captureUiError(err, { area: "Work", operation: "read_team_roster", status });
      })
      .finally(() => {
        if (read === latestRead.current) setReady(true);
      });
  }, [viewerUserId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener("work_item.updated", handler);
    window.addEventListener("tickets.invalidate", handler);
    // A Team made or joined anywhere (the one-click start included) is on the
    // roster without a reload.
    window.addEventListener(SUBSCRIPTIONS_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener("work_item.updated", handler);
      window.removeEventListener("tickets.invalidate", handler);
      window.removeEventListener(SUBSCRIPTIONS_CHANGED_EVENT, handler);
    };
  }, [fetchAll]);

  const enrichmentOf = useCallback(
    (deskId: string): TeamEnrichment | null => rows.get(deskId) ?? null,
    [rows],
  );

  const roster = useMemo(() => [...rows.values()], [rows]);

  return useMemo(() => ({ enrichmentOf, roster, ready }), [enrichmentOf, roster, ready]);
}
